import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * @typedef {Object} LensesStockEntry
 * @property {string} value
 * @property {string} optionValueId
 * @property {string | number} variantId
 * @property {string} policy
 * @property {boolean} available
 * @property {number | 'Infinity'} stock
 */

/**
 * @typedef {Object} LensesStockData
 * @property {number} threshold
 * @property {string} optionName
 * @property {LensesStockEntry[]} variants
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {Array<{ element: Element | Document | null, event: string, handler: (e: Event) => void }>} */
  #lensesEventListeners = [];
  /** @type {LensesStockData | null} */
  #lensesStockData = null;
  /** @type {number} */
  #inventoryThreshold = 0;
  /** @type {string | null} */
  #originalAtcText = null;
  /** @type {string | undefined} */
  #pendingRequestUrl;
  /** @type {boolean} */
  #isRestoring = false;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    if (this.dataset.lensesVariantPickerEnabled === 'true') {
      this.#initLensesPicker();
    }

    // Listen for page morphs triggered by components like product-links
    const onMorphed = () => {
      if (this.dataset.lensesVariantPickerEnabled === 'true') {
        this.#initLensesPicker();
      }
    };
    document.addEventListener('page:morphed', onMorphed, { signal: (this.#abortController = new AbortController()).signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up lenses picker event listeners
    if (this.#lensesEventListeners) {
      this.#lensesEventListeners.forEach(({ element, event, handler }) => {
        element?.removeEventListener(event, handler);
      });
      this.#lensesEventListeners = [];
    }
  }

  /**
   * Re-run lenses picker init after this component is morphed by other components (e.g., product-links)
   */
  updatedCallback() {
    // Call parent hook if any
    // @ts-ignore
    super.updatedCallback?.();
    if (this.dataset.lensesVariantPickerEnabled === 'true') {
      this.#initLensesPicker();
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Skip normal variant picker behavior if lenses picker is enabled
    if (this.dataset.lensesVariantPickerEnabled === 'true') {
      return;
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // Re-initialize lenses picker after DOM morph
    if (this.dataset.lensesVariantPickerEnabled === 'true') {
      this.#initLensesPicker();
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Initialize lenses picker functionality
   */
  #initLensesPicker() {
    this.#cleanupLensesListeners();
    this.#ensureItemsInputExists();
    this.#parseLensesStockData();
    this.#enableAtcByDefault();

    /** @type {HTMLInputElement | null} */
    const checkbox = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="single_eye_power"]'));
    /** @type {HTMLElement | null} */
    const singleEyeMode = /** @type {HTMLElement | null} */ (this.querySelector('.lenses-single-eye-mode'));
    /** @type {HTMLElement | null} */
    const dualEyeMode = /** @type {HTMLElement | null} */ (this.querySelector('.lenses-dual-eye-mode'));
    
    if (checkbox && singleEyeMode && dualEyeMode) {

      // Handle checkbox toggle
      const checkboxHandler = () => {
        if (checkbox.checked) {
          singleEyeMode.style.display = 'none';
          dualEyeMode.style.display = 'block';
        } else {
          singleEyeMode.style.display = 'block';
          dualEyeMode.style.display = 'none';
        }
        this.#updateLensesItemsData();
        this.#updatePriceDisplay(checkbox.checked);
        this.#updateAtcAndNotifyFromSelection();
      };
      checkbox.addEventListener('change', checkboxHandler);
      this.#lensesEventListeners.push({ element: checkbox, event: 'change', handler: checkboxHandler });

      // Handle single eye selector
      /** @type {HTMLSelectElement | null} */
      const singleEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('.lenses-single-eye-select'));
      if (singleEyeSelect) {
        /** @param {Event} event */
        const singleEyeHandler = (event) => {
          event.stopPropagation();
          this.#updateLensesItemsData();
          this.#updateAtcAndNotifyFromSelection();
        };
        singleEyeSelect.addEventListener('change', singleEyeHandler);
        this.#lensesEventListeners.push({ element: singleEyeSelect, event: 'change', handler: singleEyeHandler });
      }

      // Handle dual eye selectors
      /** @type {HTMLSelectElement | null} */
      const leftEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="left"]'));
      /** @type {HTMLSelectElement | null} */
      const rightEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="right"]'));
      
      if (leftEyeSelect && rightEyeSelect) {
        /** @param {Event} event */
        const dualEyeHandler = (event) => {
          event.stopPropagation();
          this.#updateLensesItemsData();
          this.#updateAtcAndNotifyFromSelection();
        };

        [leftEyeSelect, rightEyeSelect].forEach((select) => {
          select.addEventListener('change', dualEyeHandler);
          this.#lensesEventListeners.push({ element: select, event: 'change', handler: dualEyeHandler });
        });
      }

      // Keep lenses items data in sync when quantity changes
      const productFormForQty = this.#getProductForm();
      if (productFormForQty) {
        // Listen to the global quantity selector update event and scope to this product form
        /** @param {Event & { target: Element }} e */
        const onQtySelectorUpdate = (e) => {
          if (!(e.target instanceof Element)) return;
          if (!productFormForQty.contains(e.target)) return;
          this.#updateLensesItemsData();
        };
        document.addEventListener(ThemeEvents.quantitySelectorUpdate, onQtySelectorUpdate);
        this.#lensesEventListeners.push({ element: document, event: ThemeEvents.quantitySelectorUpdate, handler: onQtySelectorUpdate });
      }
      
      // Initial items data update (suppress storage writes during restore)
      this.#isRestoring = true;
      this.#updateLensesItemsData();
      // Apply URL override first, else restore from local storage
      const appliedOverride = this.#applyVariantOverrideFromUrl();
      if (!appliedOverride) {
        this.#restoreLensesSelectionFromStorage();
      }
      // Ensure UI reflects restored/overridden state
      this.#updateLensesItemsData();
      this.#updatePriceDisplay(checkbox.checked);
      this.#updateAtcAndNotifyFromSelection();
      // Enable storage writes and persist final resolved selection
      this.#isRestoring = false;
      this.#updateLensesItemsData();
    }
  }


  #getProductForm() {
    return this.closest('product-form-component') || document.querySelector(`product-form-component[data-section-id="${this.dataset.sectionId}"]`);
  }

  /**
   * Ensure items input exists in product form
   */
  #ensureItemsInputExists() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    // Check if items input already exists
    /** @type {HTMLInputElement | null} */
    let itemsDataInput = /** @type {HTMLInputElement | null} */ (productForm.querySelector('.lenses-items-data'));
    
    if (!itemsDataInput) {
      // Create items input and add it to the product form
      itemsDataInput = /** @type {HTMLInputElement} */ (document.createElement('input'));
      itemsDataInput.type = 'hidden';
      itemsDataInput.value = '';
      itemsDataInput.className = 'lenses-items-data';

      // Find the form element and add the input
      const form = productForm.querySelector('form');
      if (form) {
        form.appendChild(itemsDataInput);
      }
    }
  }

  /**
   * Update price display based on dual mode selection
   */
  /**
   * @param {boolean} isDualMode
   */
  #updatePriceDisplay(isDualMode) {
    const sectionId = this.dataset.sectionId;
    const productPriceElements = document.querySelectorAll(`#ProductInformation-${sectionId} product-price`);
    
    productPriceElements.forEach((priceElement) => {
      const priceContainer = priceElement.querySelector('[ref="priceContainer"]');
      if (!priceContainer) return;
      
      const priceSpan = priceContainer.querySelector('.price');
      const compareAtPriceSpan = priceContainer.querySelector('.compare-at-price');
      
      if (isDualMode) {
        // Use doubled prices
        const packPrice = this.dataset.packPrice;
        const packComparePrice = this.dataset.packComparePrice;
        
        if (priceSpan && packPrice) {
          priceSpan.innerHTML = packPrice;
        }
        
        if (compareAtPriceSpan && packComparePrice) {
          compareAtPriceSpan.innerHTML = packComparePrice;
        }
      } else {
        // Use original prices
        const originalPrice = this.dataset.price;
        const originalComparePrice = this.dataset.comparePrice;
        
        if (priceSpan && originalPrice) {
          priceSpan.innerHTML = originalPrice;
        }
        
        if (compareAtPriceSpan && originalComparePrice) {
          compareAtPriceSpan.innerHTML = originalComparePrice;
        }
      }
    });
  }

  /**
   * Update lenses items data for cart submission
   */
  #updateLensesItemsData() {
    // Early return if product form is not found
    const productForm = this.#getProductForm();
    if (!productForm) {
      console.warn('Product form not found for lenses picker');
      return;
    }
    
    /** @type {HTMLInputElement | null} */
    const itemsDataInput = /** @type {HTMLInputElement | null} */ (productForm.querySelector('.lenses-items-data'));
    if (!itemsDataInput) {
      console.warn('Items data input not found in product form');
      return;
    }
    
    /** @type {HTMLInputElement | null} */
    const checkbox = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="single_eye_power"]'));
    /** @type {HTMLSelectElement | null} */
    const singleEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('.lenses-single-eye-select'));
    /** @type {HTMLSelectElement | null} */
    const leftEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="left"]'));
    /** @type {HTMLSelectElement | null} */
    const rightEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="right"]'));
    
    // Get quantity from product form
    /** @type {HTMLInputElement | null} */
    const quantityInput = /** @type {HTMLInputElement | null} */ (productForm?.querySelector('input[name="quantity"]'));
    let quantity = 1; // Default quantity

    if (quantityInput) {
      const parsedQuantity = parseInt(quantityInput.value);
      if (!isNaN(parsedQuantity) && parsedQuantity > 0) {
        quantity = parsedQuantity;
      }
    }
    
    const isDualMode = !!checkbox?.checked;
    
    if (!isDualMode) {
      // Single eye mode
      const singleEyeValue = singleEyeSelect?.value;
      const singleEyeValid = singleEyeValue && singleEyeValue.trim() !== '';

      if (singleEyeValid) {
        const singleEyeOption = singleEyeSelect?.options[singleEyeSelect.selectedIndex];
        const variantId = singleEyeOption?.dataset.variantId;

        if (variantId) {
          const itemsData = [
            {
              id: variantId,
              quantity: quantity
            }
          ];

          itemsDataInput.value = JSON.stringify(itemsData);
          // Persist selection for single-eye mode
          this.#saveLensesSelectionToStorage(singleEyeValue);
        } else {
          itemsDataInput.value = '';
        }
      } else {
        itemsDataInput.value = '';
      }
    } else {
      // Dual eye mode
      const leftEyeValue = leftEyeSelect?.value;
      const rightEyeValue = rightEyeSelect?.value;

      const leftEyeValid = leftEyeValue && leftEyeValue.trim() !== '';
      const rightEyeValid = rightEyeValue && rightEyeValue.trim() !== '';

      if (leftEyeValid && rightEyeValid) {
        const leftEyeOption = leftEyeSelect?.options[leftEyeSelect.selectedIndex];
        const rightEyeOption = rightEyeSelect?.options[rightEyeSelect.selectedIndex];

        const leftVariantId = leftEyeOption?.dataset.variantId;
        const rightVariantId = rightEyeOption?.dataset.variantId;

        if (leftVariantId && rightVariantId) {
          const itemsData = [
            {
              id: rightVariantId,
              quantity: quantity,
              properties: { Eye: 'Right Eye' }
            },
            {
              id: leftVariantId,
              quantity: quantity,
              properties: { Eye: 'Left Eye' }
            }
          ];

          itemsDataInput.value = JSON.stringify(itemsData);
          // Persist selection for dual-eye mode
          this.#saveLensesSelectionToStorage(`${leftEyeValue},${rightEyeValue}`);
        } else {
          itemsDataInput.value = '';
        }
      } else {
        itemsDataInput.value = '';
      }
    }
    // Ensure the hidden input only participates in submission when it has a value
    if (itemsDataInput.value && itemsDataInput.value.trim() !== '') {
      itemsDataInput.name = 'items';
    } else {
      itemsDataInput.removeAttribute('name');
    }
  }

  /**
   * Save lenses selection string to localStorage under key "p"
   * @param {string} selectionString
   */
  #saveLensesSelectionToStorage(selectionString, force = false) {
    try {
      if ((force || !this.#isRestoring) && selectionString && selectionString.trim() !== '') {
        localStorage.setItem('p', JSON.stringify(selectionString));
      }
    } catch (_e) {}
  }

  /**
   * Restore lenses selection from localStorage key "p"
   */
  #restoreLensesSelectionFromStorage() {
    try {
      const raw = localStorage.getItem('p');
      if (!raw) return;
      /** @type {string} */
      let value = '';
      try {
        value = JSON.parse(raw);
      } catch {
        value = raw;
      }
      if (typeof value !== 'string' || value.trim() === '') return;

      const checkbox = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="single_eye_power"]'));
      const singleEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('.lenses-single-eye-select'));
      const leftEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="left"]'));
      const rightEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="right"]'));

      if (!checkbox || !singleEyeSelect || !leftEyeSelect || !rightEyeSelect) return;

      if (value.includes(',')) {
        const [left, right] = value.split(',');
        // Switch to dual mode
        this.#showDualMode(true);
        this.#setSelectValue(leftEyeSelect, left);
        this.#setSelectValue(rightEyeSelect, right);
      } else {
        // Single mode
        this.#showDualMode(false);
        this.#setSelectValue(singleEyeSelect, value);
      }
    } catch (_e) {}
  }

  /**
   * Apply selection override from URL ?variant=ID
   * @returns {boolean} true if applied
   */
  #applyVariantOverrideFromUrl() {
    try {
      const url = new URL(window.location.href);
      const variantId = url.searchParams.get('variant');
      if (!variantId || !this.#lensesStockData) return false;
      const entry = this.#lensesStockData.variants.find((v) => String(v.variantId) === String(variantId));
      if (!entry) return false;

      const checkbox = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="single_eye_power"]'));
      const singleEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('.lenses-single-eye-select'));
      const leftEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="left"]'));
      const rightEyeSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="right"]'));
      if (!checkbox || !singleEyeSelect || !leftEyeSelect || !rightEyeSelect) return false;

      const value = entry.value;
      if (checkbox.checked) {
        // Dual mode: set both eyes to the variant's power
        this.#setSelectValue(leftEyeSelect, value);
        this.#setSelectValue(rightEyeSelect, value);
        this.#saveLensesSelectionToStorage(`${value},${value}`, true);
      } else {
        // Single mode
        this.#setSelectValue(singleEyeSelect, value);
        this.#saveLensesSelectionToStorage(value, true);
      }
      return true;
    } catch (_e) {
      return false;
    }
  }

  /**
   * Programmatically set select value and selected attribute
   * @param {HTMLSelectElement} select
   * @param {string | undefined} value
   */
  #setSelectValue(select, value) {
    if (!select || !value) return;
    const opt = Array.from(select.options).find((o) => o.value === value);
    if (!opt) return;
    select.value = value;
    for (const o of select.options) o.removeAttribute('selected');
    opt.setAttribute('selected', 'selected');
  }

  /**
   * Toggle between single and dual eye modes
   * @param {boolean} dual
   */
  #showDualMode(dual) {
    /** @type {HTMLInputElement | null} */
    const checkbox = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="single_eye_power"]'));
    /** @type {HTMLElement | null} */
    const singleEyeMode = /** @type {HTMLElement | null} */ (this.querySelector('.lenses-single-eye-mode'));
    /** @type {HTMLElement | null} */
    const dualEyeMode = /** @type {HTMLElement | null} */ (this.querySelector('.lenses-dual-eye-mode'));
    if (!checkbox || !singleEyeMode || !dualEyeMode) return;
    checkbox.checked = dual;
    singleEyeMode.style.display = dual ? 'none' : 'block';
    dualEyeMode.style.display = dual ? 'block' : 'none';
  }

  #parseLensesStockData() {
    const stockScript = this.querySelector('script[data-lenses-stock]');
    if (!stockScript?.textContent) return;
    try {
      /** @type {LensesStockData} */
      const data = /** @type {LensesStockData} */ (JSON.parse(stockScript.textContent));
      this.#lensesStockData = data;
      const thresholdAttr = this.dataset.inventoryThreshold;
      this.#inventoryThreshold = Number(thresholdAttr ?? data.threshold ?? 0) || 0;
    } catch (e) {
      console.error('Failed to parse lenses stock data', e);
    }
  }

  #enableAtcByDefault() {
    const productForm = this.#getProductForm();
    /** @type {HTMLButtonElement | null} */
    const atc = /** @type {HTMLButtonElement | null} */ (productForm?.querySelector('button[ref="addToCartButton"]'));
    if (atc) {
      atc.disabled = false;
      /** @type {HTMLElement | null} */
      const atcText = /** @type {HTMLElement | null} */ (atc.querySelector('.add-to-cart-text__content'));
      const defaultAddText = this.dataset.addToCartText;
      if (atcText) {
        if (this.#originalAtcText == null) {
          this.#originalAtcText = defaultAddText || atcText.textContent;
        }
        if (defaultAddText) {
          atcText.textContent = defaultAddText;
        }
      }
    }
  }

  #updateAtcAndNotifyFromSelection() {
    if (!this.#lensesStockData) return;
    /** @type {HTMLInputElement | null} */
    const checkbox = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="single_eye_power"]'));
    const isDual = !!checkbox?.checked;
    let status = /** @type {'yes'|'no'|'low'|'preorder'} */ ('yes');
    let lowCount = 0;

    if (!isDual) {
      /** @type {HTMLSelectElement | null} */
      const select = /** @type {HTMLSelectElement | null} */ (this.querySelector('.lenses-single-eye-select'));
      const result = this.#calculateSingleStatusWithCount(select?.value ?? '');
      status = result.status;
      lowCount = result.count;
    } else {
      const left = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="left"]'))?.value ?? '';
      const right = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[data-eye="right"]'))?.value ?? '';
      const result = this.#calculateDualStatusWithCount(left, right);
      status = result.status;
      lowCount = result.count;
    }

    this.#applyAtcAndNotifyUi(/** @type {'yes'|'no'|'low'|'preorder'} */ (status));
    this.#updateInventoryStatusUi(status, lowCount);
  }

  /**
   * @param {('yes'|'no'|'low'|'preorder')} status
   */
  #applyAtcAndNotifyUi(status) {
    const productForm = this.#getProductForm();
    /** @type {HTMLButtonElement | null} */
    const atc = /** @type {HTMLButtonElement | null} */ (productForm?.querySelector('button[ref="addToCartButton"]'));
    /** @type {HTMLElement | null} */
    const atcText = /** @type {HTMLElement | null} */ (atc?.querySelector('.add-to-cart-text__content'));
    /** @type {HTMLElement | null} */
    const notifyButton = /** @type {HTMLElement | null} */ (document.getElementById(`NotifyMeButton-${this.dataset.sectionId}`));
    const soldOutText = this.dataset.soldOutText || 'Sold out';

    if (!atc) return;

    if (status === 'no') {
      atc.disabled = true;
      if (atcText) atcText.textContent = soldOutText;
      if (notifyButton) notifyButton.style.display = '';
    } else {
      atc.disabled = false;
      if (atcText && this.#originalAtcText != null) atcText.textContent = this.#originalAtcText;
      if (notifyButton) notifyButton.style.display = 'none';
    }
  }

  #cleanupLensesListeners() {
    if (this.#lensesEventListeners?.length) {
      this.#lensesEventListeners.forEach(({ element, event, handler }) => {
        element?.removeEventListener(event, handler);
      });
      this.#lensesEventListeners = [];
    }
  }

  /**
   * @param {string} value
   */
  #calculateSingleStatus(value) {
    if (!value) return 'yes';
    const entry = this.#findStockEntry(value);
    if (!entry) return 'yes';
    const stock = entry.stock;
    return this.#statusFromStock(stock);
  }

  /**
   * @param {string} value
   * @returns {{ status: 'yes'|'no'|'low'|'preorder', count: number }}
   */
  #calculateSingleStatusWithCount(value) {
    if (!value) return { status: 'yes', count: 0 };
    const entry = this.#findStockEntry(value);
    if (!entry) return { status: 'yes', count: 0 };
    const stock = entry.stock;
    const status = this.#statusFromStock(stock);
    const count = stock === 'Infinity' || typeof stock !== 'number' ? 0 : stock;
    return { status, count };
  }

  /**
   * @param {string} leftValue
   * @param {string} rightValue
   */
  #calculateDualStatus(leftValue, rightValue) {
    if (!leftValue || !rightValue) return 'yes';
    const left = this.#findStockEntry(leftValue);
    const right = this.#findStockEntry(rightValue);
    if (!left || !right) return 'yes';

    const leftStock = left.stock;
    const rightStock = right.stock;

    if (leftStock === 0 || rightStock === 0) return 'no';
    if (leftStock === 'Infinity' || rightStock === 'Infinity') return 'preorder';

    // If either side is low (but not OOS or preorder), whole is low
    const leftStatus = this.#statusFromStock(leftStock);
    const rightStatus = this.#statusFromStock(rightStock);
    // Special rule for same power: do NOT mark low just because pairs are <= threshold.
    // For same power, status is determined by the original stock status (not halved pairs).
    if (leftValue === rightValue) {
      return leftStatus; // leftStatus and rightStatus are identical here
    }
    // Different powers: if either side is low, overall is low
    if (leftStatus === 'low' || rightStatus === 'low') return 'low';

    let possiblePairs;
    if (leftValue === rightValue) {
      const count = typeof leftStock === 'number' ? leftStock : 0;
      possiblePairs = Math.floor(count / 2);
    } else {
      const l = typeof leftStock === 'number' ? leftStock : 0;
      const r = typeof rightStock === 'number' ? rightStock : 0;
      possiblePairs = Math.min(l, r);
    }

    if (possiblePairs <= 0) return 'no';
    if (possiblePairs <= this.#inventoryThreshold) return 'low';
    return 'yes';
  }

  /**
   * @param {string} leftValue
   * @param {string} rightValue
   * @returns {{ status: 'yes'|'no'|'low'|'preorder', count: number }}
   */
  #calculateDualStatusWithCount(leftValue, rightValue) {
    if (!leftValue || !rightValue) return { status: 'yes', count: 0 };
    const left = this.#findStockEntry(leftValue);
    const right = this.#findStockEntry(rightValue);
    if (!left || !right) return { status: 'yes', count: 0 };

    const leftStock = left.stock;
    const rightStock = right.stock;

    if (leftStock === 0 || rightStock === 0) return { status: 'no', count: 0 };
    if (leftStock === 'Infinity' || rightStock === 'Infinity') return { status: 'preorder', count: 0 };

    const leftStatus = this.#statusFromStock(leftStock);
    const rightStatus = this.#statusFromStock(rightStock);

    let possiblePairs = 0;
    if (leftValue === rightValue) {
      const count = typeof leftStock === 'number' ? leftStock : 0;
      possiblePairs = Math.floor(count / 2);
      // Special rule for same power: status from original stock (ignore pair threshold for low)
      return { status: leftStatus, count: possiblePairs };
    } else {
      const l = typeof leftStock === 'number' ? leftStock : 0;
      const r = typeof rightStock === 'number' ? rightStock : 0;
      possiblePairs = Math.min(l, r);
    }

    if (possiblePairs <= 0) return { status: 'no', count: 0 };
    if (leftStatus === 'low' || rightStatus === 'low' || possiblePairs <= this.#inventoryThreshold) {
      return { status: 'low', count: possiblePairs };
    }
    return { status: 'yes', count: possiblePairs };
  }

  /**
   * @param {number | 'Infinity'} stock
   */
  #statusFromStock(stock) {
    if (stock === 'Infinity') return 'preorder';
    if (typeof stock !== 'number') return 'yes';
    if (stock <= 0) return 'no';
    if (stock <= this.#inventoryThreshold) return 'low';
    return 'yes';
  }

  /**
   * @param {string} value
   * @returns {LensesStockEntry | undefined}
   */
  #findStockEntry(value) {
    if (!this.#lensesStockData) return undefined;
    return this.#lensesStockData.variants.find((v) => v.value === value);
  }

  /**
   * Update product inventory UI text and icon
   * @param {'yes'|'no'|'low'|'preorder'} status
   * @param {number} count
   */
  #updateInventoryStatusUi(status, count) {
    const sectionId = this.dataset.sectionId;
    /** @type {HTMLElement | null} */
    const root = /** @type {HTMLElement | null} */ (document.querySelector(`#ProductInformation-${sectionId} product-inventory`));
    if (!root) return;

    /** @type {HTMLElement | null} */
    const textEl = /** @type {HTMLElement | null} */ (root.querySelector('.product-inventory__text'));
    /** @type {HTMLElement | null} */
    const iconWrapper = /** @type {HTMLElement | null} */ (root.querySelector('[data-inventory-icon]'));
    /** @type {HTMLElement | null} */
    const check = /** @type {HTMLElement | null} */ (iconWrapper?.querySelector('.icon-check'));
    /** @type {HTMLElement | null} */
    const cross = /** @type {HTMLElement | null} */ (iconWrapper?.querySelector('.icon-cross'));
    /** @type {HTMLElement | null} */
    const subtextEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-inventory-subtext]'));

    /** @type {string} */
    let text = '';
    /** @type {string} */
    let subtext = '';
    if (status === 'no') {
      text = /** @type {any} */ (root).dataset.textOutOfStock || 'Out of stock.';
      subtext = '';
      if (check) check.style.display = 'none';
      if (cross) cross.style.display = '';
      iconWrapper?.classList.remove('product-inventory__icon-in_stock','product-inventory__icon-low','product-inventory__icon-preorder');
      iconWrapper?.classList.add('product-inventory__icon-out_of_stock');
    } else if (status === 'preorder') {
      text = /** @type {any} */ (root).dataset.textPreorder || 'Preorder.';
      if (cross) cross.style.display = 'none';
      if (check) check.style.display = '';
      subtext = /** @type {any} */ (root).dataset.subtextPreorder || '';
      iconWrapper?.classList.remove('product-inventory__icon-in_stock','product-inventory__icon-low','product-inventory__icon-out_of_stock');
      iconWrapper?.classList.add('product-inventory__icon-preorder');
    } else if (status === 'low') {
      // Prefer lenses-specific low stock template (pairs) when lenses picker is enabled
      const tpl = (/** @type {any} */ (root).dataset.textLowLensesTemplate) || root.dataset.textLowTemplate || 'Only [value] pieces left.';
      text = tpl.replace('[value]', String(count));
      if (cross) cross.style.display = 'none';
      if (check) check.style.display = '';
      subtext = /** @type {any} */ (root).dataset.subtextInStock || '';
      iconWrapper?.classList.remove('product-inventory__icon-in_stock','product-inventory__icon-preorder','product-inventory__icon-out_of_stock');
      iconWrapper?.classList.add('product-inventory__icon-low');
    } else {
      text = /** @type {any} */ (root).dataset.textInStock || 'In stock.';
      if (cross) cross.style.display = 'none';
      if (check) check.style.display = '';
      subtext = /** @type {any} */ (root).dataset.subtextInStock || '';
      iconWrapper?.classList.remove('product-inventory__icon-low','product-inventory__icon-preorder','product-inventory__icon-out_of_stock');
      iconWrapper?.classList.add('product-inventory__icon-in_stock');
    }

    if (textEl) {
      textEl.textContent = text;
    }
    if (subtextEl) {
      if (subtext) {
        subtextEl.style.display = '';
        subtextEl.textContent = subtext;
      } else {
        subtextEl.style.display = 'none';
        subtextEl.textContent = '';
      }
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
